import { useRef } from 'react';
import { useNavigate } from 'react-router';

import {
  ACCOUNT_MENU_ITEMS,
  ACCOUNT_MENU_TEXT,
  AccountMenu,
  type AccountMenuItem,
} from '@features/account-menu';
import { APP_PATH } from '@shared/config';
import { usePublishStickyTop } from '@shared/lib/sticky-top';
import { ROLE, type Role, selectIdentity, useSession } from '@shared/session';
import { openAlertDialog } from '@shared/ui/alert-dialog';
import { BackBar } from '@widgets/back-bar';
import {
  GlobalHeader,
  type GlobalHeaderNavTarget,
  type GlobalHeaderProps,
} from '@widgets/global-header';
import { GlobalSearchPanel } from '@widgets/global-search';
import { ImpersonationBanner } from '@widgets/impersonation-banner';
import { NotificationList } from '@widgets/notification-list';

import { useBackBar } from './app-back-bar.hook';
import { useEndImpersonation } from './app-impersonation.hook';
import { useAppLogout } from './app-logout.hook';
import { useAppNotification } from './app-notification.hook';
import { useAppSearch } from './app-search.hook';
import { useScreenInfo } from './screen-handle';

const HEADER_ROLE: Record<Role, GlobalHeaderProps['role']> = {
  [ROLE.D3]: 'staff',
  [ROLE.ADMIN]: 'staff',
  [ROLE.LP]: 'lp',
  [ROLE.PORTFOLIO]: 'pf',
};

/** 헤더 메뉴 아이콘 → 화면(기존 `Header.form.tsx`) */
const NAV_PATH: Record<GlobalHeaderNavTarget, string> = {
  home: APP_PATH.root,
  'portfolio-evaluation': APP_PATH.portfolioEvaluation,
  file: APP_PATH.file,
  'portfolio-report': APP_PATH.portfolioReport,
  member: APP_PATH.member,
};

/** 계정 메뉴 → 화면(기존 `Header.menu.form.tsx`). 로그아웃은 따로 */
const ACCOUNT_PATH: Record<Exclude<AccountMenuItem, 'logout'>, string> = {
  info: APP_PATH.info,
  config: APP_PATH.config,
  'access-log': APP_PATH.accessLog,
};

/**
 * T1 · T2 의 머리: 대행 배너(대행 중일 때) → GlobalHeader(역할별) → BackBar(화면이 켤 때).
 * 헤더와 배너는 위에 붙어 있고, BackBar 는 헤더 아래에 함께 붙는다(기존 코드: 고정 60 + 36).
 */
export function AppHead() {
  // 본문의 sticky 줄(분기 머리 등)이 이 머리 바로 아래에 붙도록 실제 높이를 알린다
  const headRef = useRef<HTMLDivElement>(null);
  usePublishStickyTop(headRef);
  const navigate = useNavigate();
  const identity = useSession(selectIdentity);
  const screen = useScreenInfo();
  const logout = useAppLogout();
  const endImpersonation = useEndImpersonation();
  const notification = useAppNotification();
  const search = useAppSearch();
  // BackBar = 화면 트리의 상위 화면으로(DD-52). 글자 "상위 - 현재", 상세 화면은 대상 이름(useScreenName)
  const backBar = useBackBar(screen);

  if (!identity) return null;

  const showBackBar = backBar !== null && screen?.backBar !== false;
  const impersonating = identity.impersonatorId !== undefined;
  const accountRole = identity.role === ROLE.ADMIN ? 'admin' : 'member';
  const selectAccount = (item: AccountMenuItem) => {
    // 로그아웃은 확인 뒤에(AccountMenu 청사진 — 기존 window.confirm)
    if (item === 'logout')
      void openAlertDialog({
        title: ACCOUNT_MENU_TEXT.logoutConfirm,
        confirmLabel: ACCOUNT_MENU_TEXT.logout,
      }).then((ok) => ok && logout());
    else void navigate(ACCOUNT_PATH[item]);
  };

  return (
    <div ref={headRef} className="sticky top-0 z-(--z-sticky) w-full">
      {impersonating ? (
        <ImpersonationBanner
          // 대행 대상 = 지금 세션(대행 토큰)의 신원. 이름은 서버의 회원 이름(`/auth/impersonate` 응답)
          target={{
            idx: 0,
            name: identity.name ?? '',
            type: identity.role === ROLE.LP ? 'lp' : 'pf',
          }}
          onEnd={endImpersonation}
        />
      ) : null}
      <GlobalHeader
        role={HEADER_ROLE[identity.role]}
        onNavigate={(target) => void navigate(NAV_PATH[target])}
        hasUnread={notification.hasUnread}
        onNotificationClick={notification.toggle}
        notificationPanel={
          notification.open ? (
            <NotificationList
              items={notification.items}
              lastPage={notification.lastPage}
              loadingMore={notification.loadingMore}
              onLoadMore={notification.loadMore}
              onSelect={notification.select}
              onReadAll={notification.readAll}
              hasUnread={notification.hasUnread}
            />
          ) : undefined
        }
        searchText={search.text}
        onSearchTextChange={search.setText}
        onSearchFocusChange={search.setFocused}
        searchPanel={
          search.panel ? (
            // 결과를 누를 때 검색창이 초점을 잃어 패널이 먼저 닫히지 않게 한다
            <div onMouseDown={(event) => event.preventDefault()}>
              <GlobalSearchPanel {...search.panel} />
            </div>
          ) : undefined
        }
        accountMenu={<AccountMenu role={accountRole} onSelect={selectAccount} />}
        // 태블릿(xl 미만): 설정 아이콘 대신 햄버거 메뉴에 같은 항목 · 같은 동작
        accountItems={ACCOUNT_MENU_ITEMS[accountRole].map((item) => ({
          key: item,
          label: ACCOUNT_MENU_TEXT[item],
          onSelect: () => selectAccount(item),
        }))}
      />
      {showBackBar ? (
        <BackBar title={backBar.title} onBack={() => void navigate(backBar.path)} />
      ) : null}
    </div>
  );
}
